import { useEffect, useRef, createContext, useContext, useCallback } from "react";
import { SocketClient } from "./SocketClient";

const WebSocketContext = createContext();

export default function WebSocketProvider({ user, children }) {
  const socketClientRef = useRef(null);
  const messageHandlerMapRef = useRef({});

  const connectWebSocket = useCallback((userObj) => {
    if (!userObj) return false;

    if (socketClientRef.current) {
      return true;
    }

    const serverUrl = process.env.REACT_APP_SOCKET_URL;
    socketClientRef.current = new SocketClient(`${serverUrl}/stream`, userObj);

    socketClientRef.current.addHandler((msg) => {
      const handler = messageHandlerMapRef.current[msg.messageType];
      if (handler) {
        handler(msg);
      }
    });

    socketClientRef.current.connect();
    return true;
  }, []);

  const disconnectWebSocket = useCallback(() => {
    if (socketClientRef.current) {
      socketClientRef.current.destroy();
      socketClientRef.current = null;
    }
  }, []);

  useEffect(() => {
    connectWebSocket(user);

    return () => {
      disconnectWebSocket();
    };
  }, [user, connectWebSocket, disconnectWebSocket]);

  const registerHandler = useCallback((messageType, handler) => {
    messageHandlerMapRef.current[messageType] = handler;
  }, []);

  const unregisterHandler = useCallback((messageType) => {
    delete messageHandlerMapRef.current[messageType];
  }, []);

  const contextValue = {
    registerHandler,
    unregisterHandler,
    isConnected: !!socketClientRef.current,
    connect: connectWebSocket,
    disconnect: disconnectWebSocket,
  };

  return <WebSocketContext.Provider value={contextValue}>{children}</WebSocketContext.Provider>;
}

export function useWebSocket() {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error("useWebSocket must be used within a WebSocketProvider");
  }
  return context;
}
